import {Button, SpaceBetween, Flashbar, Checkbox, Link, Alert} from '@amzn/awsui-components-react-v3';
import React from 'react';
import { ASIN_CHANNEL_TYPE_DROPDOWN_ELEMENTS, ASIN_MP_DROPDOWN_ELEMENTS } from '../../constants/asin_metric_elements';
import { REPORT_TYPE } from '../../constants/report_type';
import { PHYSICAL_CONSUMER_PRODUCT_LINES } from '../../constants/product_line';
import {
  AccuracyButtonNewStyle,
  DownloadButtonAlign,
  ReportFlashStyle, TWO_ZERO_WIDTH_SPACE,
} from '../../styles/common_styles';
import { extractErrorMessage } from '../../utilities';
import { isPublic } from '../../utils/access-utils';
import { extractComponentKey, extractSubComponentName } from '../../utils/components';
import { DATE_YYYYMM, diffYYYYMM, formatTimeFramesToYYYYMM, padZero } from '../../utils/date-utils';
import logger from '../../utils/logging';
import metricsPublisher from '../../utils/metrics';
import { AccuracyButton } from '../buttons/accuracy_button';
import { ComponentsButton } from '../buttons/components_button';
import { CurrencyButton } from '../buttons/currency_button';
import { MarketplaceSelection } from '../buttons/marketplace_selection';
import { ProductLinesButton } from '../buttons/product_lines_button';
import { SavedReportsButton } from '../buttons/saved_reports_button';
import { SnapshotsButton } from '../buttons/snapshots_button';
import { TimeFrameButton } from '../buttons/time_frame_button';
import { ASINMetricChannelItems, ChannelItems, ReportItems, RollupItems } from '../const/dropdown_elements';
import { getJSClient } from '../js_client';
import { Status } from '../main';
import { TEST_USERS } from "../../constants/constants";
import { BRIDGING_UNAGGREGATED_LIMIT } from "../reports/bridging/bridging_constants";

export const defaultPayload = {
  report: 'component',
  rollup: 'worldwide',
  region: '',
  marketplace: '',
  channel: 'ALL',
  timeFrame: '',
  productLines: 'All',
  component: 'all',
  currency: 'USD',
  channelType: '',
};

const REPORT_CAN_NOT_BE_GENEREATED_MESSAGE = 'Report can not be generated with given parameters.';

export class ReportAttributes extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      payload: Object.assign({}, defaultPayload),
      clientLoaded: false,
      formSubmitted: false,
      formLoaded: false,
      savedReportsStatus: Status.INIT,
      reportDownloading: false,
      reportDownloaded: false,
      reportDownloadError: false,
      loadReportError: false,
      loadLiveSnapshotError: false,
      reportError: '',
      user: '',
      components: [],
      reportsList: [],
      snapshots: [],
      snapshotsStatus: Status.INIT,
      selectedSnapshots: [],
      reportURL: '',
      reportSaved: false,
      queryExecutionId: null,
      queryDataScanned: null,
      key: 0,
      accessRestricted: true,
      powerUser: props.identity.powerUser,
      productLines: props.productLines,
      liveSnapshotId: this.props.appConfig.liveSnapshotId,
      liveSnapshot: {},
      startMonth: DATE_YYYYMM.START_MONTH,
      endMonth: DATE_YYYYMM.PREV_MONTH,
      invalidParametersMessage: null
    };
    this.config = {};
    this.productLineIds = [];
    this.timeFrameIds = [];
    this.timeFrameId = '';
    this.snapshotsMap = {};
    // eslint-disable-next-line prefer-destructuring
    this.rootSite = window.location.href.split('#')[0];
    this.continuousTimeFrameSelected = false;
    this.monthlyTimeFrameForQuartersSelected = false;
    this.aggregatedTimeFrameSelected = true;
    this.aggregatedMarketplaceSelected = true;
    this.aggregatedProductLineSelected = true;
    this.nonAggregatedPayloads = [];
    this.nonAggregatedFlags = {};
    this.selectedComponents = [];
    this.queryParameters = (this.props.queryParameters || {});
  }

  // eslint-disable-next-line react/sort-comp
  setSnapshots = (chosen) => {
    const newPayload = Object.assign({}, this.state.payload);
    let snapshots = [];
    let options = (this.isNonDownloadableReport() && this.state.payload.report.toUpperCase() !== REPORT_TYPE.BRIDGING_FACTORS) ? chosen.detail.selectedOptions : [chosen.detail.selectedOption];
    let startMonth = DATE_YYYYMM.START_MONTH;
    let endMonth = DATE_YYYYMM.PREV_MONTH;
    for (let option of options) {
      startMonth = Math.max(startMonth, option.data.startMonth);
      endMonth = Math.min(endMonth, option.data.endMonth);
      if (option.value === this.state.liveSnapshotId) {
        snapshots.unshift(option.data);
      } else {
        snapshots.push(option.data);
      }
      if (options.length === 1) newPayload['snapshotId'] = option.value;
    }
    if (!options.length) newPayload['snapshotId'] = this.state.liveSnapshotId;
    if (options.length > 1) {
      this.nonAggregatedPayloads = Object.assign({});
      this.aggregatedMarketplaceSelected = true;
      this.aggregatedTimeFrameSelected = true;
      this.aggregatedProductLineSelected = true;
      if (this.props.nonAggregatedPayloads && typeof this.props.nonAggregatedPayloads === 'function') {
        this.props.nonAggregatedPayloads(this.nonAggregatedPayloads);
      }
    }
    if (snapshots !== this.state.selectedSnapshots) {
      // Reset time frame selections when snapshot selections change
      this.timeFrameIds = [];
      newPayload.marketplace = '';
      let newTimeFrame = this.isNonDownloadableReport() ? '' : this.getLatestMonthFromSnapshot(snapshots);
      this.timeFrameId = newTimeFrame;
      newPayload.timeFrame = newTimeFrame;
    }

    this.setState({ selectedSnapshots: snapshots, startMonth, endMonth, payload: newPayload });    
  };

  setReportType = (chosen) => {
    const reportType = chosen.detail.selectedOption.value.toUpperCase();
    metricsPublisher.forMethod('report.' + reportType).publishCounter(1);

    const newPayload = Object.assign({}, this.state.payload);
    if (reportType === REPORT_TYPE.COMPONENT) {
      newPayload.component = 'all';
      newPayload.currency = 'USD';
      newPayload.timeFrame = this.timeFrameIds.join(',');
      newPayload.productLines = this.productLineIds.join(',');
    } else if (reportType === REPORT_TYPE.PL) {
      newPayload.currency = 'USD';
      newPayload.timeFrame = this.timeFrameIds.join(',');
      newPayload.marketplace =  '';
      newPayload.productLines = this.productLineIds.join(',');
    } else if (reportType === REPORT_TYPE.TREND) {
      newPayload.timeFrame = this.processTrendReportTimeFrame();
      newPayload.marketplace =  '';
      newPayload.productLines = this.productLineIds.join(',');
    } else if (reportType === REPORT_TYPE.BENCHMARK) {
      newPayload.rollup = 'region';
      newPayload.currency = 'LOCAL';
    } else if (reportType === REPORT_TYPE.WBR || reportType === REPORT_TYPE.HCP || reportType === REPORT_TYPE.ASIN_METRICS_WW_WBR_REPORT) {
      let newSnapshots = this.state.selectedSnapshots.length
        ? [this.state.selectedSnapshots[0]]
        : [this.state.liveSnapshot];
      this.setState({ selectedSnapshots: newSnapshots });
      newPayload.timeFrame = this.getLatestMonthFromSnapshot(newSnapshots);
      newPayload.marketplace =  '';
      newPayload.snapshotId = newSnapshots[0].id;
    } else if (reportType === REPORT_TYPE.THIRTEEN_MONTH_REPORT) {
      newPayload.marketplace =  '';
    } else if (reportType === REPORT_TYPE.ASIN_METRICS_REPORT) {
      newPayload.timeFrame = this.timeFrameId;
      newPayload.marketplace =  '';
      newPayload.productLines = '';
    } else if (reportType === REPORT_TYPE.BRIDGING_FACTORS) {
      newPayload.rollup = 'marketplace';
      newPayload.marketplace =  '';
      newPayload.currency = 'LOCAL';
    }
    newPayload.report = chosen.detail.selectedOption.value;
    this.setState({ payload: newPayload, formSubmitted: false });

    if (reportType !== REPORT_TYPE.COMPONENT) {
      this.nonAggregatedPayloads = Object.assign({});
      if (this.props.nonAggregatedPayloads && typeof this.props.nonAggregatedPayloads === 'function') {
        this.props.nonAggregatedPayloads(this.nonAggregatedPayloads);
      }
    }
  };

  getLatestMonthFromSnapshot = (snapshots = undefined) => {
    if (!snapshots) {
      snapshots = this.state.selectedSnapshots;
    }
    let snapshot = snapshots.length ? snapshots[0] : this.state.liveSnapshot;
    return (snapshot.endMonth + '').substring(0, 4) + '/' + (snapshot.endMonth + '').substring(4, 6);
  };

  setRollupBy = (chosen) => {
    let selectedId = chosen.detail.selectedOption.value;
    const newPayload = Object.assign({}, this.state.payload);
    if (selectedId === 'worldwide' || selectedId === 'region') {
      newPayload.marketplace = '';
      this.aggregatedMarketplaceSelected = true;
    }
    if (selectedId === 'worldwide' || selectedId === 'marketplace') {
      newPayload.region = '';
    }
    if (selectedId === 'marketplace') {
      newPayload.currency = 'LOCAL';
    } else {
      newPayload.currency = 'USD';
    }
    newPayload.rollup = selectedId;
    this.setState({ payload: newPayload, formSubmitted: false });
    this.setNonAggregatedPayloadsConditionally();
  };

  setRegion = (chosen) => {
    const newPayload = Object.assign({}, this.state.payload);
    newPayload.region = chosen.detail.selectedOption.value;
    this.setState({ payload: newPayload, formSubmitted: false });
    this.setNonAggregatedPayloadsConditionally();
  };

  setMarketplace = (chosen) => {
    const { detail } = chosen;
    const newPayload = Object.assign({}, this.state.payload);

    if (this.state.payload.report.toUpperCase() === REPORT_TYPE.ASIN_METRICS_REPORT) {
      newPayload.marketplace = detail.selectedOption.value;
      this.setState({ payload: newPayload, formSubmitted: false });
    } else {
      const { selectedOptions, selectedOption } = detail;
      const marketplaceIds = selectedOptions
        ? selectedOptions.map((marketplace) => parseInt(marketplace.value, 10)).join()
        : selectedOption
        ? selectedOption.value
        : '';

      if (marketplaceIds.includes(',')) {
        newPayload.currency = 'USD';
      } else {
        this.aggregatedMarketplaceSelected = true;
      }
      newPayload.marketplace = marketplaceIds;
      this.setState({ payload: newPayload, formSubmitted: false });
      this.setNonAggregatedPayloadsConditionally();
    }
  };

  setChannelType = (chosen) => {
    const newPayload = Object.assign({}, this.state.payload);
    newPayload.channel = chosen.detail.selectedOption.value;
    this.setState({ payload: newPayload, formSubmitted: false });
    this.setNonAggregatedPayloadsConditionally();
  };

  setASINChannelType = (chosen) => {
    const newPayload = Object.assign({}, this.state.payload);
    newPayload.channelType = chosen.detail.selectedOption.value;
    this.setState({ payload: newPayload, formSubmitted: false });
  };

  addContinuousMonthsAsTimeFrame = (endMonth, numOfMonth) => {
    let continuousTimeFrame = [];
    let monthToMinus = 0;
    while (monthToMinus < numOfMonth) {
      let nextMonth = new Date(endMonth / 100, (endMonth % 100) - 1 - monthToMinus++);
      continuousTimeFrame.push(nextMonth.getFullYear().toString() + '/' + padZero(nextMonth.getMonth() + 1).toString());
    }
    return continuousTimeFrame.join(',');
  };

  formatContinuousTimeFrame = () => {
    let sortedTimeFrame = formatTimeFramesToYYYYMM(this.timeFrameIds).sort();
    let numOfMonth = diffYYYYMM(sortedTimeFrame[0], sortedTimeFrame[sortedTimeFrame.length - 1]) + 1;
    return this.addContinuousMonthsAsTimeFrame(sortedTimeFrame[sortedTimeFrame.length - 1], numOfMonth);
  };

  processTrendReportTimeFrame = () => {
    const allTimeFramesAreQuarters = this.timeFrameIds.every((time) => time.includes('Q'));
    const singleMonthSelected = this.timeFrameIds.length === 1 && this.timeFrameIds[0].includes('/');

    if (allTimeFramesAreQuarters || singleMonthSelected) {
      this.continuousTimeFrameSelected = false;
    }
    if (!allTimeFramesAreQuarters || singleMonthSelected) {
      this.monthlyTimeFrameForQuartersSelected = false;
    }
    if (this.continuousTimeFrameSelected) {
      return this.formatContinuousTimeFrame();
    }
    if (this.monthlyTimeFrameForQuartersSelected) {
      return this.getMonthlyTimeFrameForQuarters();
    }
    return singleMonthSelected
      ? this.addContinuousMonthsAsTimeFrame(this.timeFrameIds[0].replace('/', '') - '0', 12)
      : this.timeFrameIds.join(',');
  };

  setTimeFrame = (chosen, type = 'accuracy') => {
    const newPayload = Object.assign({}, this.state.payload);
    if (this.isReportTypeMultiMonthAllowed() && type === 'accuracy') {
      const ids = chosen.detail.selectedOptions.map(o => o.value);
      if (ids === undefined || !ids.length) {
        this.timeFrameIds = [];
        newPayload.timeFrame = '';
      } else {
        this.timeFrameIds = ids;
        if (this.timeFrameIds.length === 1) {
          this.aggregatedTimeFrameSelected = true;
        }
        newPayload.timeFrame = ids.toString();
        if (newPayload.report === 'trend') {
          newPayload.timeFrame = this.processTrendReportTimeFrame();
        }
      }
      this.setState({ payload: newPayload, formSubmitted: false });
    } else {
      this.timeFrameId = chosen.detail.selectedOption.value;
      newPayload.timeFrame = this.timeFrameId;
      this.setState({ payload: newPayload, formSubmitted: false });
    }
    this.setNonAggregatedPayloadsConditionally();    
  };

  setProductLines = (chosen) => {
    const newPayload = Object.assign({}, this.state.payload);
    const ops = chosen.detail.selectedOptions;
    if (ops === undefined || !ops.length) {
      this.productLineIds = [];
      newPayload.productLines = 'All';
    } else {
      this.productLineIds = ops.map(o => o.value);
      newPayload.productLines = ops.map(o => o.value).toString();
    }

    if (this.state.payload.report.toUpperCase() === REPORT_TYPE.ASIN_METRICS_REPORT) {
      newPayload.gls = chosen.detail.selectedOption.gls;
      newPayload.productLines = chosen.detail.selectedOption.value;
      this.productLineIds = [chosen.detail.selectedOption.value];
    }
    this.setState({ payload: newPayload, formSubmitted: false });
    this.setNonAggregatedPayloadsConditionally();
  };

  setComponent = (chosen) => {
    const newPayload = Object.assign({}, this.state.payload);
    const selectedIds = chosen.detail.selectedOptions.map(o => o.value);
    if (selectedIds === undefined || !selectedIds.length) {
      this.selectedComponents = [];
      newPayload.component = 'all';
    } else {
      this.selectedComponents = selectedIds;
      newPayload.component = selectedIds.toString();
    }
    this.setState({ payload: newPayload, formSubmitted: false });
  };

  setCurrency = (chosen) => {
    this.setState({ payload: { ...this.state.payload, currency: chosen.detail.selectedOption.value }, formSubmitted: false });
    this.setNonAggregatedPayloadsConditionally();
  };

  componentWillMount = () => {
    this.client = getJSClient();
    this.initialClientTasks();
  };

  componentDidUpdate = (prevProps) => {
    if (this.props.authenticated && this.state.savedReportsStatus === Status.INIT) {
      if (this.props.loadID) {
        this.loadSavedReport();
      }
      this.setState({ savedReportsStatus: Status.LOADING });
      this.getSavedReports();
    }
    if (this.props.authenticated && this.state.snapshotsStatus === Status.INIT) {
      this.setState({ snapshotsStatus: Status.LOADING });
      this.getSnapshots();
    }
    if (prevProps.newReport !== this.props.newReport) {
      this.setState({ reportSaved: true, savedReportsStatus: Status.LOADING });
      this.getSavedReports();
    }
    if (prevProps.identity !== this.props.identity) {
      this.checkAccessRestrictions();
    }
    if (prevProps.productLines !== this.props.productLines) {
      this.setState({ productLines: this.props.productLines });
    }
  };

  isRegionSelected = () => this.state.payload.region !== '';

  isSnapshotMultiSelected = () => this.state.selectedSnapshots.length > 1;

  isTimeFrameSelected = () => this.state.payload.timeFrame !== '';

  isTimeFrameSelectedSingleMonth = (payload = this.state.payload) =>
    !payload.timeFrame.includes(',') && payload.timeFrame.includes('/');

  isAccuracyReportTypeSelected = () => this.state.payload.report !== '';

  isInputValidForAccuracyReport = () => this.isAccuracyReportTypeSelected() && this.isTimeFrameSelected();

  isMarketplaceSingleSelected = () =>
    this.state.payload.marketplace &&
    this.state.payload.marketplace.length > 0 &&
    !this.state.payload.marketplace.includes(',');

  isInputValidForASINMetricReport = () =>
    this.state.payload.gls &&
    this.state.payload.gls.length &&
    this.state.payload.productLines !== '' &&
    this.isTimeFrameSelected() &&
    this.isTimeFrameSelectedSingleMonth() &&
    this.state.payload.channel !== '' &&
    this.state.payload.channel.toUpperCase() !== 'ALL' &&
    this.isMarketplaceSingleSelected();

  isInputValidFor13MonthReport = () =>
    this.isInputValidForAccuracyReport() && this.isTimeFrameSelectedSingleMonth() && !this.isSnapshotMultiSelected();

  isInputValidForBenchmark = () =>
    this.isInputValidForAccuracyReport() &&
    this.isRegionSelected() &&
    this.isTimeFrameSelectedSingleMonth() &&
    !this.isSnapshotMultiSelected();

  isInputValidForBridgingReport = () =>
    this.isTimeFrameSelected() &&
    !this.isSnapshotMultiSelected()

  isReportTypeMultiMonthAllowed = () =>
    [REPORT_TYPE.COMPONENT, REPORT_TYPE.PL, REPORT_TYPE.TREND, REPORT_TYPE.BRIDGING_FACTORS, REPORT_TYPE.FORECAST].includes(this.state.payload.report.toUpperCase());

  isReportTypeSingleMonthAllowed = () =>
  // TODO allow multimonth for bridging factors
    [REPORT_TYPE.BENCHMARK, REPORT_TYPE.THIRTEEN_MONTH_REPORT, REPORT_TYPE.ASIN_METRICS_REPORT].includes(
      this.state.payload.report.toUpperCase()
    );

  isMarketplaceRequiredAndSelected = () =>
    this.state.payload.rollup === 'marketplace' ? Boolean(this.state.payload.marketplace) : true;

  isRegionRequiredAndSelected = () =>
    this.state.payload.rollup === 'region' ? Boolean(this.state.payload.region) : true;

  isNonDownloadableReport = () =>
    this.state.payload.report.toUpperCase() === REPORT_TYPE.COMPONENT ||
    this.state.payload.report.toUpperCase() === REPORT_TYPE.PL ||
    this.state.payload.report.toUpperCase() === REPORT_TYPE.TREND ||
    this.state.payload.report.toUpperCase() === REPORT_TYPE.BRIDGING_FACTORS ||
    this.state.payload.report.toUpperCase() === REPORT_TYPE.ASIN_METRICS_REPORT ||
    this.state.payload.report.toUpperCase() === REPORT_TYPE.FORECAST
  ;

  isDownloadableReport = () =>
    this.state.payload.report.toUpperCase() === REPORT_TYPE.BENCHMARK ||
    this.state.payload.report.toUpperCase() === REPORT_TYPE.THIRTEEN_MONTH_REPORT;

  isMarketplaceAllowed = () =>
    (this.state.payload.rollup === 'marketplace' && this.isNonDownloadableReport()) ||
    (this.state.payload.rollup === 'marketplace' && this.state.payload.report.toUpperCase() === REPORT_TYPE.THIRTEEN_MONTH_REPORT) ||
    this.state.payload.report.toUpperCase() === REPORT_TYPE.BRIDGING_FACTORS ||
    this.state.payload.report.toUpperCase() === REPORT_TYPE.ASIN_METRICS_REPORT;

  isAggregateAllowed = () => {
    const { payload, selectedSnapshots } = this.state;
    const { featureList } = this.props;
    return (
      (payload.report.toUpperCase() === REPORT_TYPE.COMPONENT ||
        payload.report.toUpperCase() === REPORT_TYPE.TREND ||
        payload.report.toUpperCase() === REPORT_TYPE.BRIDGING_FACTORS
      ) &&
      selectedSnapshots.length === 1 &&
      payload.marketplace.includes(',') &&
      featureList &&
      featureList.includes('slicing') &&
      !this.isAllMarketplaceSelected()
    );
  };

  isRollupAllowed = () =>
    this.state.payload.report.toUpperCase() === REPORT_TYPE.COMPONENT ||
    this.state.payload.report.toUpperCase() === REPORT_TYPE.PL ||
    this.state.payload.report.toUpperCase() === REPORT_TYPE.TREND ||
    this.state.payload.report.toUpperCase() === REPORT_TYPE.THIRTEEN_MONTH_REPORT ||
    this.state.payload.report.toUpperCase() === REPORT_TYPE.FORECAST;

  isRegionAllowed = () => {
    return (
      this.state.payload.report.toUpperCase() !== REPORT_TYPE.ASIN_METRICS_REPORT &&
      ((this.state.payload.rollup === 'region' && this.isNonDownloadableReport()) ||
        (this.state.payload.rollup === 'region' &&
          this.state.payload.report.toUpperCase() === REPORT_TYPE.THIRTEEN_MONTH_REPORT))
    );
  };

  applyButtonDisabled = () => {
    if (
      this.state.payload.report.toUpperCase() === REPORT_TYPE.WBR ||
      this.state.payload.report.toUpperCase() === REPORT_TYPE.HCP ||
      this.state.payload.report.toUpperCase() === REPORT_TYPE.ASIN_METRICS_WW_WBR_REPORT
    ) {
      return this.state.accessRestricted || !this.isInputValidForAccuracyReport() || this.isSnapshotMultiSelected();
    } else if (this.state.payload.report.toUpperCase() === REPORT_TYPE.BENCHMARK) {
      return this.state.accessRestricted || !this.isInputValidForBenchmark();
    } else if (this.state.payload.report.toUpperCase() === REPORT_TYPE.THIRTEEN_MONTH_REPORT) {
      return this.state.accessRestricted || !this.isInputValidFor13MonthReport();
    } else if (this.state.payload.report.toUpperCase() === REPORT_TYPE.ASIN_METRICS_REPORT) {
      return this.state.accessRestricted || !this.isInputValidForASINMetricReport();
    } else if (this.state.payload.report.toUpperCase() === REPORT_TYPE.BRIDGING_FACTORS) {
      return this.state.accessRestricted || !this.isInputValidForBridgingReport();
    } else {
      return (
        this.state.accessRestricted ||
        !this.isInputValidForAccuracyReport() ||
        (!this.isReportTypeMultiMonthAllowed() && !this.isTimeFrameSelectedSingleMonth()) ||
        !this.isMarketplaceRequiredAndSelected() ||
        !this.isRegionRequiredAndSelected()
      );
    }
  };

  excludeLocalCurrency = () =>
    this.state.payload.rollup === 'worldwide' ||
    (this.state.payload.rollup === 'region' && this.state.payload.report.toUpperCase() !== REPORT_TYPE.BENCHMARK) ||
    this.state.payload.marketplace.includes(',');

  initialClientTasks = () => {
    this.checkAccessRestrictions();
    this.client.getComponentConfig(this.onComponentConfigFetchSuccess, this.onConfigFetchFailure, true);
    this.setNonAggregatedPayloads();
  };

  checkAccessRestrictions = () => {
    const { identity } = this.props;
    if (identity) {
      if (!isPublic(identity)) {
        this.setState({ user: identity.username, clientLoaded: true, accessRestricted: false });
        this.props.defineUser(identity.username);
        console.log(`Authenticated user as ${this.state.user}`);
        this.props.onUserAuthentication();
      } else {
        this.setState({ accessRestricted: true, clientLoaded: true });
        logger.warn(`User ${this.props.identity.username} does not have access`);
        console.log('No user identified. Restricting access.');
      }
    } else {
      this.setState({ accessRestricted: true });
      console.log('No identity given. Waiting on client identity call...');
    }
  };

  parseReportID = (id) => {
    // Find report id after removing the user name and splitter '-'.
    // (does not make assumptions about report name).

    //Find first index of splitter
    let splitter = id.indexOf('-');
    let user = id.substring(0, splitter);

    //Check if user contain hyphen(only Cypress user)
    for (let usr of TEST_USERS) {
      if (id.includes(usr)) {
        id = id.replace(usr, '');
        //Update splitter if name has hyphen
        splitter = id.indexOf('-');
        //Update user if splitter is updated
        user = usr;
        break;
      }
    }

    const name = id.substring(splitter + 1);
    return { user, name };
  };

  getSavedReports = () => {
    const payload = {
      user: this.state.user,
      name: '',
    };
    this.client.pullSavedReports(
      (reportsResult) => {
        this.props.setReportsList(reportsResult.reportsList);
        this.setState({
          reportsList: reportsResult.reportsList,
          savedReportsStatus: Status.SUCCEEDED,
        });
      },
      (error) => {
        console.log(error);
        this.setState({ savedReportsStatus: Status.FAILED });
      },
      payload
    );
  };

  redirectSavedReport = (chosen) => {
    console.log(`Redirecting to report: ${chosen.detail.selectedOption.value}`);
    const url = `${this.rootSite}#/reports/${chosen.detail.selectedOption.value}`;
    const win = window.open(url, '_blank');

    if (win) {
      // If browser blocks popup then `win` will be `null`
      win.focus();
    } else {
      logger.warn(`URL could not be opened: ${url}`);
    }
  };

  loadSavedReport = () => {
    logger.info('Load saved report', { report: this.state.payload.report });
    this.client.pullSavedReports(
      (reportResult) => {
        if (!reportResult.reportsList) {
          this.setState({ loadReportError: true });
        } else {
          const report = reportResult.reportsList[0];
          this.populateParams(report);

          if (this.state.payload.timeFrame) {
            this.submitForm();
          }
        }
      },
      (error) => {
        console.log(error);
        this.setState({ loadReportError: true });
      },
      this.parseReportID(decodeURIComponent(this.props.loadID))
    );
  };

  populateParams = (report) => {
    console.log('Loading report ' + report.id);
    this.setReportType(this.wrapParameter(report.report));
    this.setRollupBy(this.wrapParameter(report.rollup));
    this.setRegion(this.wrapParameter(report.region));
    this.setMarketplace(this.wrapParameterStringArray(report.marketplace));
    this.setChannelType(this.wrapParameter(report.channel));
    this.setProductLines(this.wrapParameterStringArray(report.productLines));
    this.setComponent(this.wrapParameterStringArray(report.component));
    this.setCurrency(this.wrapParameter(report.currency));

    const loadSets = report.columnSets && report.columnSets.length;
    this.props.setColumnSetsToLoad(loadSets ? report.columnSets.split(',') : []);

    const loadOverrides = report.columnSetOverrides && report.columnSetOverrides.length;
    this.props.setColumnSetOverridesToLoad(loadOverrides ? report.columnSetOverrides.split(',') : []);

    this.setSnapshots(this.wrapParameterObjArray(report.snapshots));
    if (this.isReportTypeMultiMonthAllowed()) {
      this.setTimeFrame(this.wrapParameterStringArray(report.timeFrame));
    } else {
      this.setTimeFrame(this.wrapParameter(report.timeFrame));
    }
    if (typeof report.aggregatedMarketplaces === 'boolean' && typeof report.aggregatedTimeFrames === 'boolean') {
      this.aggregatedMarketplaceSelected = report.aggregatedMarketplaces;
      this.aggregatedTimeFrameSelected = report.aggregatedTimeFrames;
      if (report.report === 'component' && (!this.aggregatedTimeFrameSelected || !this.aggregatedMarketplaceSelected)) {
        this.setNonAggregatedPayloads();
      }
    }
  };

  wrapParameter = (param) => {
    if (!param) {
      param = '';
    }
    return { detail: { selectedOption: {value: param} } };
  };

  wrapParameterObjArray = (param) => {
    let options = [];
    if (param && param.length) {
      for (let obj of param) {
        options.push({ data: obj });
      }
    }
    return { detail: { selectedOptions: options } };
  };

  wrapParameterStringArray = (param) => {
    let options = [];
    if (param && param.length) {
      for (let obj of param.split(',')) {
        options.push({ value: obj });
      }
    }
    return { detail: { selectedOptions: options } };
  };

  getSnapshots = () => {
    const payload = {};
    this.client.pullSnapshots(
      (snapshotsResult) => {
        const snapshots = [];
        let liveSnapshot = {};
        let selectedSnapshots;
        let newPayload = Object.assign({}, this.state.payload);
        for (const snapshot of snapshotsResult.configList) {
          this.snapshotsMap[snapshot.id] = snapshot;
          if (snapshot.id === this.state.liveSnapshotId) {
            liveSnapshot = snapshot;
            if (this.props.liveSnapshotCallback && typeof this.props.liveSnapshotCallback === 'function') {
              this.props.liveSnapshotCallback(liveSnapshot);
            }
          }
          if (snapshot.enabled) snapshots.push(snapshot);
        }
        if (this.props.snapshotsLoadCallback && typeof this.props.snapshotsLoadCallback === 'function') {
          this.props.snapshotsLoadCallback(snapshots);
        }
        selectedSnapshots = this.state.selectedSnapshots.filter((snapshot) => {
          return this.snapshotsMap.hasOwnProperty(snapshot.id) && this.snapshotsMap[snapshot.id].enabled;
        });
        if (!selectedSnapshots.length) {
          const { snapshotId: snapshotIdFromQuery } = this.queryParameters
          const snapshotFromQuery = snapshotIdFromQuery 
                                    && this.snapshotsMap.hasOwnProperty(snapshotIdFromQuery) 
                                    && this.snapshotsMap[snapshotIdFromQuery].enabled
                                    ? this.snapshotsMap[snapshotIdFromQuery]
                                    : null;
          selectedSnapshots = [snapshotFromQuery || liveSnapshot];
          newPayload['snapshotId'] = snapshotFromQuery ? snapshotFromQuery.id : liveSnapshot.id;
        }

        if (Object.keys(liveSnapshot).length == 0) {
          logger.error('Live snapshot '+this.state.liveSnapshotId +' not found.');
          this.setState({ loadLiveSnapshotError : true });
        } else {
          this.setState({
            snapshots,
            liveSnapshot,
            selectedSnapshots,
            payload: newPayload,
            startMonth: Number(liveSnapshot.startMonth),
            endMonth: Number(liveSnapshot.endMonth),
            snapshotsStatus: Status.SUCCEEDED,
          });
        }
      },
      (error) => {
        console.log(error);
        this.setState({ snapshotsStatus: Status.FAILED });
      },
      payload
    );
  };

  createSnapshotsOptions = (snapshots) => {
    let snapshotsOptions = [];
    for (let snapshot of snapshots) {
      let dropdownItem = {};
      dropdownItem['value'] = snapshot.id;
      dropdownItem['label'] = snapshot.id === this.state.liveSnapshotId ? 'live' : snapshot.name;
      dropdownItem['data'] = snapshot;
      snapshotsOptions.push(dropdownItem);
    }
    return snapshotsOptions;
  };

  onComponentConfigFetchSuccess = (response) => {
    try {
      this.config.components = JSON.parse(response.componentConfig);
      const components = [];
      components.push({
        label: 'All',
        value: 'All',
        id: 'all',
      });
      for (const component of this.config.components) {
        const id = component.id.trim();
        const athenaColumn = component.athenaColumn.trim();
        components.push({
          label: id,
          value: id,
          id: athenaColumn,
          hasBaseFactor: (component.baseFactor && component.baseFactor.length > 0)
        });

        const hasSubComponents = component.subComponents && this.props.featureList.includes('subcomponent_' + id);
        const hasBaseFactor = component.baseFactor && component.baseFactor.length > 0;
        if (hasSubComponents) {
          for (const subComponent of component.subComponents) {
            components.push({
              label: TWO_ZERO_WIDTH_SPACE +`${extractSubComponentName(subComponent)}`,
              value: subComponent,
              id: subComponent,
              hasBaseFactor: hasBaseFactor,
            });
          }
        }
      }

      this.setState({
        components,
      });
    } catch (e) {
      console.log(e);
    }
  };

  getMarketplaceOptions = () => {
    const { marketplaceConfig } = this.props;

      const enabledMarketplacesConfig = marketplaceConfig.filter((mp) => mp.enabled);
      const f3MarketplacesConfig = this.hasAccessToF3Marketplaces()
        ? marketplaceConfig.filter((mp) => (mp.tags || []).includes('f3'))
        : [];
      const allMarketplacesConfig = this.isAllMarketplacesAllowed() ? marketplaceConfig.filter((mp) => (mp.id === "0")) : [];
      return [...enabledMarketplacesConfig, ...f3MarketplacesConfig, ...allMarketplacesConfig].map((mp) => {
        return { label: (mp.detail || '').replace('Prime Now', 'F3'), id: mp.detail, value: mp.id };
      });
  };

  getRegionOptions = () => {
    const { marketplaceConfig } = this.props;

      const enabledMarketplacesConfig = marketplaceConfig.filter((mp) => mp.enabled);
      const regionIds = [...new Set(enabledMarketplacesConfig.map(item => item.region))];
      return regionIds.map((region) => {
        return {label: region, id: region.toLowerCase(), value: region.toLowerCase()}
      });
  };

  getRegionOptions = () => {
    const { marketplaceConfig } = this.props;

      const enabledMarketplacesConfig = marketplaceConfig.filter((mp) => mp.enabled);
      const regionIds = [...new Set(enabledMarketplacesConfig.map(item => item.region))];
      return regionIds.map((region) => {
        return {label: region, id: region.toLowerCase(), value: region.toLowerCase()}
      });
  };

  onConfigFetchFailure = (error) => {
    logger.error('Config fetch failed', error);
  };

  prepareSnapshots = () => {
    if (this.state.selectedSnapshots.length) {
      return this.state.selectedSnapshots;
    } else {
      return [this.state.liveSnapshot];
    }
  };

  submitForm = () => {
    metricsPublisher.forMethod('submitReport').publishCounter(1);
    logger.info('Submit report with payload', {
      payload: this.state.payload,
      snapshots: this.state.selectedSnapshots.map((s) => s.id),
    });
    this.setState({ loadStartTimestamp: performance.now() });
    this.clearFlash();
    // eslint-disable-next-line react/no-unused-state
    if (this.state.payload.report.toUpperCase() === REPORT_TYPE.WBR) {
      this.downloadWWReport();
    } else if (this.state.payload.report.toUpperCase() === REPORT_TYPE.HCP) {
      this.downloadHCPSpotReport();
    } else if (this.state.payload.report.toUpperCase() === REPORT_TYPE.BENCHMARK) {
      this.downloadBenchmark();
    } else if (this.state.payload.report.toUpperCase() === REPORT_TYPE.THIRTEEN_MONTH_REPORT) {
      this.downloadReport();
    } if (this.state.payload.report.toUpperCase() === REPORT_TYPE.ASIN_METRICS_WW_WBR_REPORT) {
      this.downloadAsinWWWBRReport();
    } else {
      this.props.resetFunction();
      this.props.callback(this.state.payload, this.prepareSnapshots());
    }
    this.setState({ formSubmitted: true });
    console.log('Form submitted!');
  };

  downloadReport = () => {
    logger.info('Download report with payload', { payload: this.state.payload });
    this.setState({ reportDownloading: true, reportDownloadError: false });
    this.client.downloadAccuracyReport(
      async (report) => {
        console.log(
          'Received response: %s,%s,%s ',
          report.queryExecutionStatus,
          report.queryExecutionId,
          report.queryDataScanned
        );

        // eslint-disable-next-line react/no-unused-state
        this.setState({ queryDataScanned: report.queryDataScanned });
        if (report.queryExecutionStatus === 'INCOMPLETE') {
          this.setState({ queryExecutionId: report.queryExecutionId });
          await this.sleep(1000);
          console.log(`Resend request: ${this.state.queryExecutionId}`);
          this.downloadReport();
        } else {
          window.open(report.data, '_blank');
          this.setState({
            reportDownloading: false,
            reportDownloaded: true,
          });
        }
      },
      (error) => {
        logger.error('Error while download report', error, this.state.payload);
        let message = extractErrorMessage(error);
        if (message === 'Network Error') {
          message = 'Report is being prepared, try again after some time';
        }
        this.setState({
          reportDownloading: false,
          reportDownloadError: true,
          reportError: message,
        });
        console.log(error);
      },
      this.state.payload,
      this.state.queryExecutionId
    );
  };

  sleep = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

  downloadBenchmark = () => {
    logger.info('Download benchmark with payload', this.state.payload);
    this.setState({ reportDownloading: true, reportDownloadError: false });
    this.client.downloadAccuracyBenchmark(
      (report) => {
        window.open(report.data, '_blank');
        this.setState({
          reportDownloading: false,
          reportDownloaded: true,
        });
      },
      (error) => {
        logger.error('Error while download report', error, this.state.payload);
        let message = extractErrorMessage(error);
        if (message === 'Network Error') {
          message = 'Report is being prepared, try again after some time';
        }
        this.setState({
          reportDownloading: false,
          reportDownloadError: true,
          reportError: message,
        });
        console.log(error);
      },
      this.state.payload
    );
  };

  downloadWWReport = () => {
    const { payload } = this.state;

    logger.info('Download ww report with payload', { payload });
    this.setState({ reportDownloading: true });
    this.client.downloadWWReport(
      (report) => {
        window.open(report.data, '_blank');
        this.setState({
          reportDownloading: false,
          reportDownloaded: true,
        });
        this.setState({ loadEndTimestamp: performance.now() });
        logger.info('Report response time', {
          responseTime: this.state.loadEndTimestamp - this.state.loadStartTimestamp,
          report: payload ? payload.report : '',
        });
      },
      (error) => {
        logger.error('Error while download report', error, payload);
        let message = extractErrorMessage(error);
        if (message === 'Network Error') {
          message = 'Report is being prepared, try again after some time';
        }
        this.setState({
          reportDownloading: false,
          reportDownloadError: true,
          reportError: message,
        });
        console.log(error);
      },
      this.state.payload
    );
  };

  downloadAsinWWWBRReport = () => {
    const { payload } = this.state;

    logger.info('Download asin ww report with payload', { payload });
    this.setState({ reportDownloading: true });
    this.client.downloadAsinWWWBRReport(
      (report) => {
        window.open(report.data, '_blank');
        this.setState({
                        reportDownloading: false,
                        reportDownloaded: true,
                      });
        this.setState({ loadEndTimestamp: performance.now() });
        logger.info('Report response time', {
          responseTime: this.state.loadEndTimestamp - this.state.loadStartTimestamp,
          report: payload ? payload.report : '',
        });
      },
      (error) => {
        logger.error('Error while download report', error, payload);
        let message = extractErrorMessage(error);
        if (message === 'Network Error') {
          message = 'Report is being prepared, try again after some time';
        }
        this.setState({
                        reportDownloading: false,
                        reportDownloadError: true,
                        reportError: message,
                      });
        console.log(error);
      },
      this.state.payload
    );
  };

  downloadHCPSpotReport = () => {
    const { payload } = this.state;

    logger.info('Download hcpSpot report with payload', { payload });
    this.setState({ reportDownloading: true });
    this.client.downloadHCPSpotReport(
      (report) => {
        window.open(report.data, '_blank');
        this.setState({ reportDownloading: false, reportDownloaded: true });
        this.setState({ loadEndTimestamp: performance.now() });
        logger.info('Report response time', {
          responseTime: this.state.loadEndTimestamp - this.state.loadStartTimestamp,
          report: payload ? payload.report : '',
        });
      },
      (error) => {
        logger.error('Error while download report', error, payload);
        this.setState({
          reportDownloading: false,
          reportDownloadError: true,
          reportError: extractErrorMessage(error),
        });
        console.log(error);
      },
      this.state.payload
    );
  };

  clearForm = () => {
    let newPayload = Object.assign({}, defaultPayload);
    newPayload['report'] = this.state.payload.report;
    newPayload['snapshotId'] = this.state.liveSnapshotId;
    newPayload['timeFrame'] = this.isNonDownloadableReport()
      ? ''
      : this.getLatestMonthFromSnapshot([this.state.liveSnapshot]);
    this.setState({
      payload: newPayload,
      selectedSnapshots: [this.state.liveSnapshot],
      startMonth: this.snapshotsMap[this.state.liveSnapshotId].startMonth,
      endMonth: this.snapshotsMap[this.state.liveSnapshotId].endMonth,
    });
    this.timeFrameId = this.isNonDownloadableReport() ? '' : this.getLatestMonthFromSnapshot([this.state.liveSnapshot]);
    this.timeFrameIds = [];
    this.productLineIds = [];
    this.nonAggregatedPayloads = [];
    this.aggregatedTimeFrameSelected = true;
    this.aggregatedMarketplaceSelected = true;
    this.clearFlash();
    this.props.resetFunction();
  };

  clearFlash = () => {
    console.log('resetting form without changing selections');
    this.setState({
      key: this.state.key + 1,
      reportDownloaded: false,
      reportDownloadError: false,
      // eslint-disable-next-line react/no-unused-state
      formSubmitted: false,
      reportSaved: false,
    });
  };

  S3LocationFlash = () => {
    if (!this.state.clientLoaded) {
      return (
        <Flashbar
          items={[
            {
              type: 'info',
              loading: true,
              content: 'Verifying user access'
            },
          ]}
      />);
    } else if (this.state.accessRestricted) {
      return (
          <Flashbar
              items={[
                {
                  type: 'error',
                  content: 'No access to the reports'
                },
              ]}
          />);
    } else if (this.state.reportDownloading) {
      return (
          <Flashbar
              items={[
                {
                  type: 'success',
                  loading: true,
                  dismissible: false,
                  content: "Loading report..."
                },
              ]}
          />);
    } else if (this.state.reportDownloaded) {
      return (
          <Flashbar
              items={[
                {
                  type: 'success',
                  dismissible: true,
                  content: "Report Downloaded.",
                  onDismiss: () => this.clearFlash
                },
              ]}
          />);
    } else if (this.state.reportDownloadError) {
      const errorText = this.state.reportError;
      return (
          <Flashbar
              items={[
                {
                  type: 'error',
                  dismissible: true,
                  content: `${errorText}`,
                  onDismiss: () => this.clearFlash
                },
              ]}
          />);
    } else if (this.state.loadReportError) {
      return (
          <Flashbar
              items={[
                {
                  type: 'error',
                  dismissible: true,
                  content: "Sorry, the requested report could not be found.",
                  onDismiss: () => this.clearFlash
                },
              ]}
          />);
    } else if (this.state.loadLiveSnapshotError) {
      return (
        <Flashbar
          items={[
            {
              type: 'error',
              dismissible: true,
              content: "Sorry, Live snapshot not found. See console error for details.",
              onDismiss: () => this.clearFlash
            },
          ]}
        />);
    } else if (this.state.reportSaved) {
      const link = `${this.rootSite}#/reports/${this.props.newReport}`;
      return (
          <Flashbar
              items={[
                {
                  type: 'success',
                  dismissible: true,
                  content: (
                      <div>
                        Report saved. You can access it at{' '}
                        <Link color="inverted" href={link} target="_blank">
                          {link}
                        </Link>
                      </div>
                  ),
                  onDismiss: () => this.clearFlash
                },
              ]}
          />
      );
    }
    return <div />;
  };

  prepareTooltipText = () => {
    let snapshotId = this.state.payload.snapshotId;
    let snapshots = this.state.selectedSnapshots;
    let tooltipText = '';
    if (this.isNonDownloadableReport()) {
      tooltipText = 'Required Input: Type, Time Frame.';
    }

    if (snapshots.length > 1) {
      if (this.isNonDownloadableReport()) {
        snapshots.forEach((currentSnapshot) => {
          tooltipText = this.addSnapshotTooltip(currentSnapshot.id, tooltipText);
        });
      } else {
        tooltipText += ' \n Currently only one snapshot is allowed for WW WBR and HCP-Spot reports';
      }
      return tooltipText;
    }
    return this.addSnapshotTooltip(snapshotId, tooltipText);
  };

  addSnapshotTooltip = (snapshotId, tooltipText) => {
    if (this.snapshotsMap[snapshotId]) {
      let snapshot = this.snapshotsMap[snapshotId];
      let snapshotName = snapshot.id === this.state.liveSnapshotId ? 'live' : snapshot.name;
      tooltipText += ` \n Snapshot ${snapshotName} only contains data from ${this.formatFromYYYYMM(
        snapshot.startMonth
      )} to ${this.formatFromYYYYMM(snapshot.endMonth)}`;
    }
    return tooltipText;
  };

  formatFromYYYYMM = (date) => {
    return date.toString().slice(4) + '/' + date.toString().slice(0, 4);
  };

  setContinuousTimeFrame = (event) => {
    const newPayload = Object.assign({}, this.state.payload);
    if (event.detail.checked) {
      this.continuousTimeFrameSelected = true;
      newPayload.timeFrame = this.formatContinuousTimeFrame();
    } else {
      this.continuousTimeFrameSelected = false;
      newPayload.timeFrame = this.timeFrameIds.toString();
    }
    this.setState({ payload: newPayload });
  };

  getMonthlyTimeFrameForQuarters = () => {
    return formatTimeFramesToYYYYMM(this.timeFrameIds)
      .map((time) => {
        return time.toString().slice(0, 4) + '/' + time.toString().slice(4);
      })
      .join(',');
  };

  setMonthlyTimeFrameForQuarters = (event) => {
    const newPayload = Object.assign({}, this.state.payload);
    if (event.detail.checked) {
      this.monthlyTimeFrameForQuartersSelected = true;
      newPayload.timeFrame = this.getMonthlyTimeFrameForQuarters();
    } else {
      this.monthlyTimeFrameForQuartersSelected = false;
      newPayload.timeFrame = this.timeFrameIds.toString();
    }
    this.setState({ payload: newPayload });
  };

  setNonAggregatedPayloadsConditionally = () => {
    if (this.nonAggregatedPayloads.length && this.state.selectedSnapshots.length === 1) {
      this.setNonAggregatedPayloads();
    }
  };

  setNonAggregatedPayloads = () => {
    this.nonAggregatedPayloads = [];
    if (this.aggregatedMarketplaceSelected === false && this.aggregatedTimeFrameSelected === false) {
      let marketplaces = this.state.payload.marketplace.split(',');
      let comb = [];
      for (let a of marketplaces) {
        for (let b of this.timeFrameIds) {
          comb.push(a + ',' + b);
        }
      }
      for (let k = 0; k < comb.length; k++) {
        this.nonAggregatedPayloads[k] = Object.assign({}, this.state.payload);
        this.nonAggregatedPayloads[k].marketplace = comb[k].split(',')[0];
        this.nonAggregatedPayloads[k].timeFrame = comb[k].split(',')[1];
        this.nonAggregatedPayloads[k].currency = 'USD';
        this.nonAggregatedPayloads[k].region = this.state.payload.region;
      }
    } else if (this.aggregatedMarketplaceSelected === false && this.aggregatedTimeFrameSelected === true) {
      let marketplaces = this.state.payload.marketplace.split(',');
      for (let i = 0; i < marketplaces.length; i++) {
        this.nonAggregatedPayloads[i] = Object.assign({}, this.state.payload);
        this.nonAggregatedPayloads[i].currency = 'USD';
        this.nonAggregatedPayloads[i].marketplace = marketplaces[i];
        this.nonAggregatedPayloads[i].region = this.state.payload.region;
      }
    } else if (this.aggregatedMarketplaceSelected === true && this.aggregatedTimeFrameSelected === false) {
      for (let i = 0; i < this.timeFrameIds.length; i++) {
        this.nonAggregatedPayloads[i] = Object.assign({}, this.state.payload);
        this.nonAggregatedPayloads[i].timeFrame = this.timeFrameIds[i];
        this.nonAggregatedPayloads[i].currency = 'USD';
        this.nonAggregatedPayloads[i].region = this.state.payload.region;
      }
    } else {
      console.log('No Non-Aggregate Payloads found');
      this.nonAggregatedPayloads = Object.assign({});
    }
    logger.info('Report Slicing', { nonAggregatedPayloads: this.nonAggregatedPayloads });
    if (this.props.nonAggregatedPayloads && typeof this.props.nonAggregatedPayloads === 'function') {
      this.props.nonAggregatedPayloads(this.nonAggregatedPayloads);
    }
    if (this.props.nonAggregatedFlags && typeof this.props.nonAggregatedFlags === 'function') {
      this.nonAggregatedFlags['aggregatedMarketplaceSelected'] = this.aggregatedMarketplaceSelected;
      this.nonAggregatedFlags['aggregatedTimeFrameSelected'] = this.aggregatedTimeFrameSelected;
      this.nonAggregatedFlags['aggregatedProductLineSelected'] = this.aggregatedProductLineSelected;
      this.props.nonAggregatedFlags(this.nonAggregatedFlags);
    }
  };

  setNonAggregatedTimeFrame = (event) => {
    this.aggregatedTimeFrameSelected = !!event.detail.checked;
    this.setNonAggregatedPayloads();
  };

  setNonAggregatedMarketplace = (event) => {
    this.aggregatedMarketplaceSelected = !!event.detail.checked;
    this.setNonAggregatedPayloads();
  };

  setNonAggregatedProductLine = (event) => {
    this.aggregatedProductLineSelected = !!event.detail.checked;
    this.setNonAggregatedPayloads();
  }

  hasAccessToF3Marketplaces = () => {
    return this.props.featureList && this.props.featureList.includes('f3_marketplaces');
  };

  isAllMarketplacesAllowed = () => {
    return (this.state.payload.report.toUpperCase() === REPORT_TYPE.COMPONENT ||
      this.state.payload.report.toUpperCase() === REPORT_TYPE.PL ||
      this.state.payload.report.toUpperCase() === REPORT_TYPE.FORECAST) &&
      this.state.selectedSnapshots.length === 1 &&
      this.props.featureList && this.props.featureList.includes('all_marketplaces');
  };

  isAllMarketplaceSelected = () => {
    return this.state.payload.marketplace && this.state.payload.marketplace.split(",").includes("0");
  };

  isAsinMetricsReportType = () => {
    return this.state.payload.report.toUpperCase() === REPORT_TYPE.ASIN_METRICS_REPORT ||
      this.state.payload.report.toUpperCase() === REPORT_TYPE.ASIN_METRICS_WW_WBR_REPORT;
  };

  resolveStartEndMonth = () => {

    const { report } = this.state.payload;
    const { startMonth, endMonth, selectedSnapshots, liveSnapshot } = this.state;

    if (report.toUpperCase() === REPORT_TYPE.ASIN_METRICS_REPORT) {
      //TODO: remove hardcoded start/end month after date range is determined for ASIN Metrics Report
      return { startMonth: 202001, endMonth: DATE_YYYYMM.PREV_MONTH }
    }

    if (report.toUpperCase() === REPORT_TYPE.BRIDGING_FACTORS) {

      const snapshots = selectedSnapshots && selectedSnapshots.length ? selectedSnapshots : [liveSnapshot];
      const startDates = snapshots.filter(s => s.bridgingParameters).map(s => parseInt(s.bridgingParameters.startDate));
      const endDates = snapshots.filter(s => s.bridgingParameters).map(s => parseInt(s.bridgingParameters.endDate));

      // Bridging Report doesn't allow selecting multiple snapshots - 
      // but if it did (in future) - take the latest start date and the earliest end date
      // to get the overlapping dates between snapshots.
      const maxStartDate = startDates && startDates.length ? Math.max(...startDates) : -1;
      const minEndDate = maxStartDate && endDates && endDates.length ? Math.min(...endDates): -1;

      // For the case when the time ranges don't overlap - maxStartDate will be greater than minEndDate.
      // In that case  - the selected snapshots can't be reasonably compared.
      const startDate = maxStartDate > minEndDate ? -1: maxStartDate;
      const endDate = maxStartDate > minEndDate ? -1: minEndDate;

      return { startMonth: startDate, endMonth: endDate }
    }
    
    return { startMonth, endMonth };
  }

  render() {
    let accessibleReports = this.props.featureList
      ? ReportItems.filter((r) => this.props.featureList.includes(r.value))
      : [];
    
    const { startMonth, endMonth } = this.resolveStartEndMonth();
    const invalidParametersMessage = !(startMonth > 0 && endMonth > 0) ? REPORT_CAN_NOT_BE_GENEREATED_MESSAGE : null;    
    
    return (
      <div style={{ minHeight: '1000px'}}>
        <SavedReportsButton
          text="Saved Reports"
          reportsList={this.state.reportsList}
          onChange={this.redirectSavedReport}
          loading={this.state.savedReportsStatus === Status.INIT || this.state.savedReportsStatus === Status.LOADING}
          disabled={this.state.accessRestricted || this.state.savedReportsStatus === Status.FAILED}
        />
        {this.props.featureList &&
          this.props.featureList.includes('snapshots') &&
          !this.isAsinMetricsReportType() && (
            <SnapshotsButton
              text="Snapshots"
              options={this.createSnapshotsOptions(this.state.snapshots)}
              onChange={this.setSnapshots}
              loading={this.state.snapshotsStatus === Status.INIT || this.state.snapshotsStatus === Status.LOADING}
              disabled={this.state.snapshotsStatus === Status.FAILED}
              selected={this.state.selectedSnapshots}
              multiselect={this.isNonDownloadableReport() && this.state.payload.report.toUpperCase() !== REPORT_TYPE.BRIDGING_FACTORS}
              liveSnapshotId={this.state.liveSnapshotId}
            />
          )}
        <AccuracyButton
          text="Report Type"
          items={accessibleReports}
          onChange={this.setReportType}
          selected={this.state.payload.report}
          data-cy="report-type-button"
        />
        {this.isRollupAllowed() && (
          <AccuracyButton
            text="Rollup"
            items={RollupItems}
            onChange={this.setRollupBy}
            selected={this.state.payload.rollup}
            data-cy="rollup-button"
          />
        )}
        {this.isMarketplaceAllowed() && (
          <div className={AccuracyButtonNewStyle}>
            <MarketplaceSelection
              text="Marketplaces"
              options={this.state.payload.report.toUpperCase() !== REPORT_TYPE.ASIN_METRICS_REPORT ?
                       this.getMarketplaceOptions() : ASIN_MP_DROPDOWN_ELEMENTS}
              onChange={this.setMarketplace}
              selected={this.state.payload.marketplace}
              multiselect={this.state.payload.report.toUpperCase() !== REPORT_TYPE.ASIN_METRICS_REPORT}
              required={this.state.payload.report.toUpperCase() !== REPORT_TYPE.BRIDGING_FACTORS}
              disabled={this.state.payload.report.toUpperCase() === REPORT_TYPE.BRIDGING_FACTORS && !this.aggregatedMarketplaceSelected && this.state.payload.marketplace.split(',').length >= BRIDGING_UNAGGREGATED_LIMIT}
            />
            {this.isAggregateAllowed() && (
              <Checkbox
                onChange={this.setNonAggregatedMarketplace}
                checked={this.aggregatedMarketplaceSelected}
              >
                Aggregate
              </Checkbox>
            )}
          </div>
        )}
        {(this.isRegionAllowed() || this.state.payload.report.toUpperCase() === REPORT_TYPE.BENCHMARK) && (
          <AccuracyButton
            text="Region"
            items={this.getRegionOptions()}
            onChange={this.setRegion}
            selected={this.state.payload.region}
            data-cy="region-button"
            required={true}
          />
        )}
        {((this.isNonDownloadableReport() &&
          this.state.payload.report.toUpperCase() !== REPORT_TYPE.BRIDGING_FACTORS) ||
          this.state.payload.report.toUpperCase() === REPORT_TYPE.THIRTEEN_MONTH_REPORT) && (
          <AccuracyButton
            text="Channel"
            required={this.state.payload.report.toUpperCase() === REPORT_TYPE.ASIN_METRICS_REPORT}
            items={
              this.state.payload.report.toUpperCase() === REPORT_TYPE.ASIN_METRICS_REPORT
                ? ASINMetricChannelItems
                : ChannelItems
            }
            onChange={this.setChannelType}
            selected={this.state.payload.channel}
            data-cy="channel-button"
          />
        )}
        {this.state.payload.report.toUpperCase() === REPORT_TYPE.ASIN_METRICS_REPORT &&
        (<AccuracyButton
          text="Channel Type"
          required={false}
          items={ASIN_CHANNEL_TYPE_DROPDOWN_ELEMENTS}
          onChange={this.setASINChannelType}
          selected={this.state.payload.channelType}
          data-cy="asin-channel-type-button"
        /> )}
        <div className={AccuracyButtonNewStyle}>
          {this.isReportTypeMultiMonthAllowed() && (
            <TimeFrameButton
              text="Time Frame"
              startMonth={startMonth}
              endMonth={endMonth}
              onChange={this.setTimeFrame}
              selected={this.timeFrameIds}
              multiSelect={true}
              data-cy="time-frame-button"
              required={this.isNonDownloadableReport() || this.isDownloadableReport()}
              disabled={invalidParametersMessage || 
                this.state.payload.report.toUpperCase() === REPORT_TYPE.BRIDGING_FACTORS 
                && !this.aggregatedTimeFrameSelected 
                && this.timeFrameIds.length >= BRIDGING_UNAGGREGATED_LIMIT}
            />
          )}
          {this.isReportTypeSingleMonthAllowed() && (
            <TimeFrameButton
              text="Time Frame"
              startMonth={startMonth}
              endMonth={endMonth}
              onChange={this.setTimeFrame}
              selected={this.timeFrameId}
              multiSelect={false}
              data-cy="time-frame-button"
              required={this.isNonDownloadableReport() || this.isDownloadableReport()}
              disabled={invalidParametersMessage}
            />
          )}
          {(this.state.payload.report.toUpperCase() === REPORT_TYPE.COMPONENT ||
            this.state.payload.report.toUpperCase() === REPORT_TYPE.BRIDGING_FACTORS) &&
            this.state.selectedSnapshots.length === 1 &&
            this.timeFrameIds.length > 1 &&
            this.props.featureList &&
            this.props.featureList.includes('slicing') && !this.isAllMarketplaceSelected() && (
              <Checkbox
                onChange={this.setNonAggregatedTimeFrame}
                checked={this.aggregatedTimeFrameSelected}
              >
                Aggregate
              </Checkbox>
            )}
          {this.state.payload.report.toUpperCase() === REPORT_TYPE.TREND &&
            this.timeFrameIds.length > 1 &&
            !this.timeFrameIds.every((time) => time.includes('Q')) && (
              <Checkbox
                onChange={this.setContinuousTimeFrame}
                checked={this.continuousTimeFrameSelected}
              >
                Use all months in the range
              </Checkbox>
            )}
          {this.state.payload.report.toUpperCase() === REPORT_TYPE.TREND &&
            this.timeFrameIds.length > 0 &&
            this.timeFrameIds.every((time) => time.includes('Q')) && (
              <Checkbox
                onChange={this.setMonthlyTimeFrameForQuarters}
                checked={this.monthlyTimeFrameForQuartersSelected}
              >
                Display months separately
              </Checkbox>
            )}
        </div>
        {(this.isNonDownloadableReport() ||
          this.state.payload.report.toUpperCase() === REPORT_TYPE.THIRTEEN_MONTH_REPORT) && (
          <ProductLinesButton
            text="Product Lines"
            required={this.state.payload.report.toUpperCase() === REPORT_TYPE.ASIN_METRICS_REPORT}
            productLines={this.state.productLines}
            selected={this.productLineIds}
            onChange={this.setProductLines}
            multiselect={this.state.payload.report.toUpperCase() !== REPORT_TYPE.ASIN_METRICS_REPORT}
            productLineParentFilter={this.state.payload.report.toUpperCase() === REPORT_TYPE.BRIDGING_FACTORS ? PHYSICAL_CONSUMER_PRODUCT_LINES : []}
            disabled={this.state.payload.report.toUpperCase() === REPORT_TYPE.BRIDGING_FACTORS && !this.aggregatedProductLineSelected && this.productLineIds.length >= BRIDGING_UNAGGREGATED_LIMIT}
          />
        )}
        <div className={AccuracyButtonNewStyle}>
          {(this.state.payload.report.toUpperCase() === REPORT_TYPE.BRIDGING_FACTORS &&
            this.state.selectedSnapshots.length === 1 &&
            this.productLineIds.length > 1 &&
            this.props.featureList &&
            this.props.featureList.includes('slicing')) && (
              <Checkbox
                onChange={this.setNonAggregatedProductLine}
                checked={this.aggregatedProductLineSelected}
              >
                Aggregate
              </Checkbox>
            )}
        </div>
        {(this.state.payload.report.toUpperCase() === REPORT_TYPE.PL ||
        this.state.payload.report.toUpperCase() === REPORT_TYPE.FORECAST) && (
          <ComponentsButton
            text="Component"
            components={this.state.components}
            reportType={this.state.payload.report}
            onChange={this.setComponent}
            selected={this.selectedComponents}
            extractParentComponent={extractComponentKey}
            data-cy="component-button"
          />
        )}
        {(this.state.payload.report.toUpperCase() === REPORT_TYPE.COMPONENT ||
          this.state.payload.report.toUpperCase() === REPORT_TYPE.BRIDGING_FACTORS ||
          this.state.payload.report.toUpperCase() === REPORT_TYPE.PL ||
          this.state.payload.report.toUpperCase() === REPORT_TYPE.FORECAST ||
          this.isDownloadableReport()) && (
          <CurrencyButton
            text="Currency"
            onChange={this.setCurrency}
            selected={this.state.payload.currency}
            excludeLocal={this.excludeLocalCurrency()}
            report={this.state.payload.report}
          />
        )}
        <div className={DownloadButtonAlign}>
          <SpaceBetween direction="horizontal" size="xs">
          <Button variant="normal" onClick={this.clearForm} > Clear </Button>
            <Button
              variant="primary"
              disabled={this.applyButtonDisabled()}
              onClick={this.submitForm}
              data-cy="calculate-button"
            >
              Apply
            </Button>
            {/* this.applyButtonDisabled() && (<Popover
                size="medium"
                triggerType="custom"
                content={this.prepareTooltipText()}
            >
              <Icon
                  name="status-info"
                  size="small"
                  variant="subtle"
              />
            </Popover> ) */}
          </SpaceBetween>
        </div>
        <div className={ReportFlashStyle} key={this.state.key}>
          {this.S3LocationFlash()}
        </div>
        <div>
          {invalidParametersMessage && <Alert type='error'>
            {invalidParametersMessage}
          </Alert>}
        </div>
        <div data-cy="void-area" style={{ height: '1px' }} />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/navigation/report_attributes.jsx